import type { Root } from "mdast";

/**
 * Parse attributes from GitBook shortcode
 * Example: style="info" title="Example" -> { style: "info", title: "Example" }
 */
function parseAttributes(attrString: string): Record<string, string> {
  const attributes: Record<string, string> = {};
  const attrRegex = /(\w+)="([^"]*)"/g;
  let match;

  while ((match = attrRegex.exec(attrString)) !== null) {
    attributes[match[1]] = match[2];
  }

  return attributes;
}

/**
 * Remark plugin that identifies and converts GitBook-style shortcodes (such as hints or tips marked with {% hint %}...{% endhint %})
 * in Markdown into custom AST nodes, so they can be rendered as styled components in React.
 */
export default function remarkGitbookBlocks() {
  return (tree: Root) => {
    const newChildren: any[] = [];
    let collecting = false;
    let collected: any[] = [];
    let currentName: string | null = null;
    let currentAttrs: Record<string, string> = {};

    // Helper function to check if an end tag exists anywhere in the remaining tree
    const hasEndTagInTree = (
      shortcodeName: string,
      startIndex: number
    ): boolean => {
      for (let j = startIndex; j < tree.children.length; j++) {
        const futureNode = tree.children[j];
        if (futureNode.type === "paragraph") {
          const futureText = futureNode.children
            .map((child: any) => (child.type === "text" ? child.value : ""))
            .join("");
          if (futureText.includes(`{% end${shortcodeName} %}`)) {
            return true;
          }
        }
      }
      return false;
    };

    for (let i = 0; i < tree.children.length; i++) {
      const node = tree.children[i];

      // Check if this is a paragraph
      if (node.type === "paragraph") {
        // Get ALL text content from the paragraph, not just first child
        const allText = node.children
          .map((child: any) => {
            if (child.type === "text") return child.value;
            return "";
          })
          .join("");

        // First, check for self-closing shortcodes (like {% embed url="..." %})
        // Look for the pattern anywhere in the text, not just exact match
        const selfClosingMatch = allText.match(/{%\s*(\w+)([^%]*?)%}/);
        if (selfClosingMatch && !collecting) {
          // Check if this is truly a self-closing tag (no corresponding end tag)
          const shortcodeName = selfClosingMatch[1];
          const hasEndTagInCurrentParagraph = allText.includes(
            `{% end${shortcodeName} %}`
          );
          const hasEndTagLater = hasEndTagInTree(shortcodeName, i);

          // Only treat as self-closing if there's no end tag anywhere
          if (!hasEndTagInCurrentParagraph && !hasEndTagLater) {
            const attrString = selfClosingMatch[2];
            const attributes = parseAttributes(attrString.trim());

            // If there's content before or after the shortcode, we need to split the paragraph
            const beforeMatch = allText.substring(0, selfClosingMatch.index);
            const afterMatch = allText.substring(
              selfClosingMatch.index! + selfClosingMatch[0].length
            );

            // Add any content before the shortcode
            if (beforeMatch.trim()) {
              newChildren.push({
                type: "paragraph",
                children: [{ type: "text", value: beforeMatch }],
              });
            }

            // Add the gitbook node
            const gitbookNode = {
              type: "gitbook-block",
              data: {
                hName: `gitbook-${shortcodeName}`,
                hProperties: attributes,
              },
              children: [], // Self-closing, no children
            };
            newChildren.push(gitbookNode);

            // Add any content after the shortcode
            if (afterMatch.trim()) {
              newChildren.push({
                type: "paragraph",
                children: [{ type: "text", value: afterMatch }],
              });
            }

            continue; // Skip the original node
          }
        }

        // Second, check if this paragraph contains a complete GitBook shortcode block (single paragraph case)
        const completeBlockMatch = allText.match(
          /^{%\s*(\w+)([^%]*?)%}([\s\S]*?){%\s*end\1\s*%}$/
        );

        if (completeBlockMatch && !collecting) {
          const [, shortcodeName, attrString] = completeBlockMatch;
          const attributes = parseAttributes(attrString.trim());

          // Create a custom GitBook block node that preserves the AST structure
          const filteredChildren = node.children.filter((child: any) => {
            if (child.type === "text") {
              const text = child.value;
              // Remove the start and end tags from text nodes
              const cleanedText = text
                .replace(/^{%\s*\w+[^%]*?%}\s*/, "") // Remove start tag
                .replace(/\s*{%\s*end\w+\s*%}$/, ""); // Remove end tag

              if (cleanedText.trim()) {
                child.value = cleanedText;
                return true;
              }
              return false;
            }
            return true; // Keep non-text nodes (links, etc.)
          });

          const gitbookNode = {
            type: "gitbook-block",
            data: {
              hName: `gitbook-${shortcodeName}`,
              hProperties: attributes,
            },
            children:
              filteredChildren.length > 0
                ? [
                    {
                      type: "paragraph",
                      children: filteredChildren,
                    },
                  ]
                : [],
          };

          newChildren.push(gitbookNode);
          continue; // Skip the original node
        }

        // Handle multi-paragraph case (the case where the shortcode is spread across multiple paragraphs):
        // check for start tag
        const startMatch = allText.match(/^{%\s*(\w+)([^%]*?)%}/);
        if (startMatch && !collecting) {
          collecting = true;
          currentName = startMatch[1];
          currentAttrs = parseAttributes(startMatch[2].trim());
          collected = [];

          // Extract content after the start tag in the same paragraph
          const startTagEnd =
            allText.indexOf(startMatch[0]) + startMatch[0].length;
          const contentAfterTag = allText.substring(startTagEnd).trim();

          if (contentAfterTag) {
            // Filter out the start tag from the node's children and keep the rest
            const filteredChildren = node.children.filter((child: any) => {
              if (child.type === "text") {
                const cleanedText = child.value.replace(
                  /^{%\s*\w+[^%]*?%}\s*/,
                  ""
                );
                if (cleanedText.trim()) {
                  child.value = cleanedText;
                  return true;
                }
                return false;
              }
              return true;
            });

            if (filteredChildren.length > 0) {
              collected.push({
                type: "paragraph",
                children: filteredChildren,
              });
            }
          }
          continue; // Skip this node
        }

        // Check for end tag
        const endMatch = allText.match(/{%\s*end(\w+)\s*%}$/);
        if (endMatch && collecting && endMatch[1] === currentName) {
          // Extract content before the end tag
          const endTagStart = allText.indexOf(endMatch[0]);
          const contentBeforeTag = allText.substring(0, endTagStart).trim();

          if (contentBeforeTag) {
            // Filter out the end tag from the node's children
            const filteredChildren = node.children.filter((child: any) => {
              if (child.type === "text") {
                const cleanedText = child.value.replace(
                  /\s*{%\s*end\w+\s*%}$/,
                  ""
                );
                if (cleanedText.trim()) {
                  child.value = cleanedText;
                  return true;
                }
                return false;
              }
              return true;
            });

            if (filteredChildren.length > 0) {
              collected.push({
                type: "paragraph",
                children: filteredChildren,
              });
            }
          }

          // Create the GitBook block
          const gitbookNode = {
            type: "gitbook-block",
            data: {
              hName: `gitbook-${currentName}`,
              hProperties: { ...currentAttrs },
            },
            children: collected,
          };

          newChildren.push(gitbookNode);

          // Reset state
          collecting = false;
          currentName = null;
          currentAttrs = {};
          collected = [];
          continue; // Skip this node
        }
      }

      // If we're collecting, add to collected nodes
      if (collecting) {
        collected.push(node);
      } else {
        // Not collecting, add to new children
        newChildren.push(node);
      }
    }

    // If we were collecting but never found an end tag, add collected nodes back
    if (collecting && collected.length > 0) {
      newChildren.push(...collected);
    }

    tree.children = newChildren;
  };
}
